import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../api/client';
import type { User } from '../api/types';
import { useMe } from '../me';

export default function UsersPage() {
  const me = useMe();
  const [users, setUsers] = useState<User[]>([]);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ username: '', password: '', is_admin: false });

  async function load() {
    try {
      const r = await api<User[]>('/admin/users');
      setUsers(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'load failed');
    }
  }
  useEffect(() => {
    void load();
  }, []);

  async function create(e: FormEvent) {
    e.preventDefault();
    setError('');
    setInfo('');
    if (form.password.length < 12) {
      setError('password must be at least 12 chars');
      return;
    }
    setCreating(true);
    try {
      await api('/admin/users', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      setForm({ username: '', password: '', is_admin: false });
      setInfo(`user "${form.username}" created`);
      void load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'create failed');
    } finally {
      setCreating(false);
    }
  }

  async function del(id: number, username: string) {
    if (!confirm(`delete user "${username}" (id=${id})?`)) return;
    setError('');
    setInfo('');
    try {
      await api(`/admin/users/${id}`, { method: 'DELETE' });
      setInfo(`deleted user_id=${id}`);
      void load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'delete failed');
    }
  }

  async function reset(id: number) {
    const np = prompt(`new password for user_id=${id} (>= 12 chars):`);
    if (!np) return;
    if (np.length < 12) {
      setError('password must be at least 12 chars');
      return;
    }
    try {
      await api(`/admin/users/${id}/password`, {
        method: 'POST',
        body: JSON.stringify({ new_password: np }),
      });
      setInfo(`password reset for user_id=${id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'reset failed');
    }
  }

  return (
    <>
      <div className="card">
        <h1>users</h1>
        {error && <div className="error-banner">{error}</div>}
        {info && <div className="success-banner">{info}</div>}
        <table>
          <thead>
            <tr>
              <th>id</th>
              <th>username</th>
              <th>role</th>
              <th>created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>{u.id}</td>
                <td>{u.username}</td>
                <td>{u.is_admin ? 'admin' : 'user'}</td>
                <td className="muted">{new Date(u.created_at * 1000).toLocaleString()}</td>
                <td style={{ textAlign: 'right' }}>
                  <button className="secondary" onClick={() => reset(u.id)}>
                    reset pw
                  </button>{' '}
                  {u.id !== me.user_id && (
                    <button className="secondary" onClick={() => del(u.id, u.username)}>
                      delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h2>create user</h2>
        <form onSubmit={create}>
          <label className="label">Username</label>
          <input
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            required
          />
          <div style={{ height: 12 }} />
          <label className="label">Password (&ge; 12 chars)</label>
          <input
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required
            minLength={12}
          />
          <div style={{ height: 12 }} />
          <label style={{ fontSize: 13 }}>
            <input
              type="checkbox"
              checked={form.is_admin}
              onChange={(e) => setForm({ ...form, is_admin: e.target.checked })}
              style={{ width: 'auto', marginRight: 6 }}
            />
            grant admin
          </label>
          <div className="actions">
            <button type="submit" disabled={creating}>
              {creating ? 'creating…' : 'create user'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
